if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        if (!this.classList.contains('am-media-viewer')) return;
        this.addEventListener('click', (event) => {
          if (this.dragged) return;
          const step = event.target.closest('[data-viewer-step]');
          const preview = event.target.closest('[data-viewer-target]');
          if (step) this.step(Number(step.dataset.viewerStep));
          else if (preview) this.select(preview.dataset.viewerTarget);
          else if (event.target.closest('[data-viewer-zoom]') || event.target.matches('.product-media-modal__content > img.active')) this.zoom(event);
          else if (event.target.matches('.product-media-modal__content') && !this.hasAttribute('data-zoomed')) this.hide();
        });
        this.addEventListener('keydown', (event) => {
          if (event.target.closest('video, iframe') || this.hasAttribute('data-zoomed')) return;
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          event.preventDefault();
          this.step(event.key === 'ArrowRight' ? 1 : -1);
        });
        this.addEventListener('pointerdown', (event) => {
          if (!event.target.matches('.product-media-modal__content > img.active')) return;
          const content = this.querySelector('[role="document"]');
          this.pointer = { x: event.clientX, y: event.clientY, left: content.scrollLeft, top: content.scrollTop };
          if (this.hasAttribute('data-zoomed') && event.pointerType === 'mouse') {
            event.preventDefault();
            content.setPointerCapture(event.pointerId);
          }
        });
        this.addEventListener('pointermove', (event) => {
          if (!this.pointer || !this.hasAttribute('data-zoomed') || event.pointerType !== 'mouse') return;
          const content = this.querySelector('[role="document"]');
          const x = event.clientX - this.pointer.x;
          const y = event.clientY - this.pointer.y;
          if (Math.abs(x) + Math.abs(y) > 4) this.dragged = true;
          content.scrollLeft = this.pointer.left - x;
          content.scrollTop = this.pointer.top - y;
        });
        this.addEventListener('pointerup', (event) => {
          if (!this.pointer) return;
          const x = event.clientX - this.pointer.x;
          const y = event.clientY - this.pointer.y;
          if (!this.hasAttribute('data-zoomed') && event.pointerType !== 'mouse' && Math.abs(x) > 50 && Math.abs(x) > Math.abs(y) * 1.5) {
            this.dragged = true;
            this.step(x < 0 ? 1 : -1);
          }
          this.pointer = null;
          setTimeout(() => { this.dragged = false; }, 0);
        });
        this.addEventListener('pointercancel', () => { this.pointer = null; this.dragged = false; });
      }

      hide() {
        this.imageSelection = (this.imageSelection || 0) + 1;
        this.removeAttribute('data-image-state');
        this.querySelector('[role="document"]')?.removeAttribute('aria-busy');
        const loading = this.querySelector('[data-viewer-loading]');
        if (loading) loading.hidden = true;
        this.removeAttribute('data-zoomed');
        super.hide();
        if (this.scrollPosition) window.scrollTo({ ...this.scrollPosition, behavior: 'instant' });
      }

      show(opener) {
        if (this.classList.contains('am-media-viewer')) this.scrollPosition = { left: window.scrollX, top: window.scrollY };
        super.show(opener);
        this.showActiveMedia();
        if (this.classList.contains('am-media-viewer')) this.querySelector('[id^="ModalClose-"]').focus({ preventScroll: true });
      }

      showActiveMedia() {
        if (this.classList.contains('am-media-viewer')) {
          this.select(this.openedBy.dataset.mediaId);
          return;
        }
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute('data-media-id')}"])`
        ).forEach((element) => {
          element.classList.remove('active');
        });
        const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`);
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');
        activeMedia.scrollIntoView();

        const container = this.querySelector('[role="document"]');
        container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }

      select(id) {
        const selection = this.imageSelection = (this.imageSelection || 0) + 1;
        const loading = this.querySelector('[data-viewer-loading]');
        if (loading) loading.hidden = true;
        this.removeAttribute('data-image-state');
        window.pauseAllMedia();
        this.removeAttribute('data-zoomed');
        const content = this.querySelector('[role="document"]');
        content.removeAttribute('aria-busy');
        const items = [...content.children].filter(item => item.hasAttribute('data-media-id'));
        const active = items.find(item => item.dataset.mediaId === String(id)) || items[0];
        if (!active) return;
        this.activeMedia = active;
        for (const item of items) {
          item.classList.toggle('active', item === active);
          item.hidden = item !== active;
        }
        active.style.removeProperty('padding-top');
        for (const image of active.matches('img') ? [active] : active.querySelectorAll('img')) {
          image.loading = 'eager';
          image.draggable = false;
        }
        for (const preview of this.querySelectorAll('[data-viewer-target]')) {
          if (preview.dataset.viewerTarget === active.dataset.mediaId) preview.setAttribute('aria-current', 'true');
          else preview.removeAttribute('aria-current');
        }
        this.querySelector('[data-viewer-count]').textContent = `${items.indexOf(active) + 1} / ${items.length}`;
        const zoom = this.querySelector('[data-viewer-zoom]');
        zoom.disabled = active.tagName !== 'IMG';
        zoom.setAttribute('aria-pressed', 'false');
        zoom.setAttribute('aria-label', 'Ampliar detalhes');
        content.scrollTo(0, 0);
        if (active.tagName === 'IMG' && (!active.complete || !active.naturalWidth)) {
          this.dataset.imageState = 'loading';
          content.setAttribute('aria-busy', 'true');
          zoom.disabled = true;
          if (loading) {
            loading.hidden = false;
            loading.querySelector('[data-viewer-loading-label]').textContent = 'Carregando imagem…';
          }
          const finish = (failed) => {
            if (selection !== this.imageSelection || !this.hasAttribute('open') || !active.isConnected) return;
            this.dataset.imageState = failed ? 'error' : 'ready';
            content.removeAttribute('aria-busy');
            zoom.disabled = failed;
            if (loading) {
              loading.hidden = !failed;
              if (failed) loading.querySelector('[data-viewer-loading-label]').textContent = 'Não foi possível carregar esta imagem. Selecione outra foto ou abra novamente.';
            }
          };
          active.decode().then(() => finish(false), () => finish(true));
        }
        if (active.tagName === 'DEFERRED-MEDIA') {
          if (!active.hasAttribute('loaded')) active.loadContent(false);
          else {
            active.querySelector('video')?.play().catch(() => {});
            for (const frame of active.querySelectorAll('iframe')) {
              const message = frame.classList.contains('js-youtube') ? { event: 'command', func: 'playVideo', args: [] } : { method: 'play' };
              frame.contentWindow?.postMessage(JSON.stringify(message), new URL(frame.src).origin);
            }
          }
        }
      }

      step(direction) {
        const items = [...this.querySelector('[role="document"]').children].filter(item => item.hasAttribute('data-media-id'));
        const index = items.indexOf(this.activeMedia);
        const next = items[(index + direction + items.length) % items.length];
        if (next) this.select(next.dataset.mediaId);
      }

      zoom(event) {
        if (this.activeMedia?.tagName !== 'IMG' || this.dataset.imageState === 'loading' || this.dataset.imageState === 'error') return;
        const zoomed = this.toggleAttribute('data-zoomed');
        const button = this.querySelector('[data-viewer-zoom]');
        button.setAttribute('aria-pressed', String(zoomed));
        button.setAttribute('aria-label', zoomed ? 'Ajustar imagem à tela' : 'Ampliar detalhes');
        const content = this.querySelector('[role="document"]');
        const bounds = content.getBoundingClientRect();
        const x = event.target === this.activeMedia ? (event.clientX - bounds.left) / bounds.width : .5;
        const y = event.target === this.activeMedia ? (event.clientY - bounds.top) / bounds.height : .5;
        content.scrollTo(zoomed ? Math.max(0, content.scrollWidth * x - content.clientWidth / 2) : 0, zoomed ? Math.max(0, content.scrollHeight * y - content.clientHeight / 2) : 0);
      }
    }
  );
}
